/**
 * Data-source selection and kiosk bootstrap (Phase 3).
 *
 * Mode rules (docs/offline/migration-strategy.md - one code path per build):
 *  - Browser: 'api', UNLESS it has completed Phase 8 offline enrolment
 *    (OfflineSetup.tsx) on a browser that actually supports the OPFS/WASM
 *    driver - see posDataMode() below.
 *  - Android kiosk (bridge ≥ 1.1): 'local' - SQLite is the source of truth
 *    for the POS surface, online or offline.
 *  - Rollback: setting localStorage['kortex.posDataMode'] = 'api' on a kiosk
 *    forces the API path (the Phase 3 safety valve; remove after validation).
 *
 * Local bootstrap (deterministic, gated - nothing renders the POS before it):
 *  1. resume() the device database (the file itself pins device/tenant/
 *     property - a wiped WebView storage cannot orphan it, and an OPFS file
 *     survives a browser restart the same way);
 *  2. on NOT_PROVISIONED, enrol once while online: fetch the receipt-grade
 *     property profile (GET /api/pos/property, added for this), mint the
 *     device identity, open the database and apply a provisioning snapshot
 *     from the existing read APIs;
 *  3. on later online boots, refresh the snapshot in the background.
 *
 * The enrolment-lite device code here is INTERIM (documented in ADR-011):
 * Phase 4 device registration assigns server-issued codes; until sync
 * exists, locally minted numbers never leave the device, so a code
 * collision cannot yet matter.
 *
 * Phase 8 addendum (docs/offline/PHASE-8-ARCHITECTURE.md): an ordinary
 * browser tab reaches 'local' mode only through OfflineSetup.tsx, which
 * requires a live session and switches this on explicitly - never silently,
 * and never on a browser OPFS/WASM cannot actually support (isOpfsSqlite
 * Supported()). `provisionLocalDatabase`/`openOrProvisionLocalDatabase` are
 * exported so OfflineSetup can reuse the exact same enrolment path this
 * module already uses for the kiosk, rather than a second implementation
 * that could drift from it.
 */
import { toPaise } from '@kortex/shared';
import {
  LocalDatabase,
  LocalDataError,
  OpfsSqliteDriver,
  SyncEngine,
  applyProvisioningSnapshot,
  type DeviceContext,
  type MenuItemDto,
  type ProvisioningSnapshot,
  type SqlDriver,
} from '@kortex/pos-local';
import { createOpfsWorker, isOpfsSqliteSupported } from '@kortex/pos-local/browser';
import { api } from '../api';
import { isAndroidKiosk } from '../androidBridge';
import { cachedDeviceAccess } from '../deviceAccess';
import { interimDeviceCode, persistedDeviceId } from '../deviceId';
import type { Session } from '../auth';
import { HttpSyncTransport } from '../syncTransport';
import { startDeviceHeartbeat, stopDeviceHeartbeat } from '../deviceHeartbeat';
import { LocalPosDataSource } from './local';
import { RemoteDataSource } from './remote';
import type { PosDataMode, PosDataSource } from './types';

export type * from './types';

const MODE_OVERRIDE_KEY = 'kortex.posDataMode';

/**
 * Set only by OfflineSetup.tsx once enrolment (PIN + capability grant +
 * provisioned local database) has actually completed - never toggled any
 * other way, so a browser can never end up in 'local' mode with nothing
 * behind it. Read on every boot by posDataMode(); cleared if the browser no
 * longer supports OPFS (a browser update or a different machine) via the
 * isOpfsSqliteSupported() check below, not by rewriting this flag itself.
 */
const BROWSER_OFFLINE_MODE_KEY = 'kortex.offlineModeEnabled';

/**
 * The one OPFS file this app ever opens in a browser. OfflineSetup.tsx (at
 * enrolment) and this module (at every later boot) must agree on the exact
 * name, or a browser enrolled by one would never resume in the other -
 * there is deliberately only the one constant, imported by both, rather
 * than two copies of a literal string.
 */
export const OFFLINE_DB_FILENAME = 'kortex-pos.sqlite3';

export function browserOfflineModeEnabled(): boolean {
  try {
    return window.localStorage.getItem(BROWSER_OFFLINE_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setBrowserOfflineModeEnabled(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(BROWSER_OFFLINE_MODE_KEY, 'true');
    else window.localStorage.removeItem(BROWSER_OFFLINE_MODE_KEY);
  } catch {
    // Storage unavailable: the flag can't stick, same as MODE_OVERRIDE_KEY
    // above - the browser falls back to 'api' every time, which is safe.
  }
}

/**
 * EXPERIMENT, not yet a settled architecture change (see thread history -
 * ADR-002 chose the native bridge for Android back in Phase 2, before this
 * OPFS/WASM driver existed at all): both the kiosk and an ordinary browser
 * now get the same OPFS/WASM driver, backed by its own dedicated Worker
 * (Phase 8; see opfs.ts's file doc for why the worker is spun up here
 * rather than reused - each driver instance owns exactly one worker for
 * its own lifetime).
 *
 * The kiosk used to take `BridgeSqliteDriver` -> window.KortexAndroid ->
 * LocalStore.kt (native framework SQLite). That path has a live,
 * unresolved bug: LocalStore.kt's returnsRows heuristic (deciding
 * "query" vs "execSQL" purely from the SQL text's leading keyword)
 * misclassifies some statement reachable from POS Billing and throws
 * "(code 0 SQLITE_OK): Queries can be performed using SQLiteDatabase
 * query or rawQuery methods only" - reproduced repeatedly, not isolated to
 * a specific statement despite exhaustively scanning every SQL literal in
 * this package. OpfsSqliteDriver has no equivalent heuristic to get wrong
 * - sqlite-wasm's own prepare/step API doesn't need one - and it already
 * works in a plain browser tab on this exact device. If the WebView (a
 * real, current Chromium under the hood - androidx.webkit) turns out not
 * to support OPFS/Workers the same way, `isAndroidKiosk()` staying wired
 * into posDataMode() below (unchanged) means we fall back to 'api' cleanly
 * rather than getting stuck - this only ever runs once that has already
 * returned 'local'.
 *
 * If this holds up, the follow-up cleanup is: update ADR-002, then remove
 * BridgeSqliteDriver, the native `database` bridge method, and LocalStore.kt
 * as dead code - not done yet, on purpose, until this is confirmed working.
 */
function createLocalDriver(): SqlDriver {
  return new OpfsSqliteDriver(OFFLINE_DB_FILENAME, createOpfsWorker());
}

/**
 * `userId` is who is signed in. On the Android app it matters: a device that is
 * not registered, where registration is not required of this person, runs online
 * only (lib/deviceAccess.ts). Without a remembered answer the app is a terminal,
 * which is how it always behaved, so an offline start is never made worse.
 */
export function posDataMode(userId?: string | null): PosDataMode {
  if (isAndroidKiosk()) {
    try {
      if (window.localStorage.getItem(MODE_OVERRIDE_KEY) === 'api') return 'api';
    } catch {
      // Storage unavailable: fall through to the kiosk default.
    }
    if (cachedDeviceAccess(userId) === 'online') return 'api';
    return 'local';
  }
  // Phase 8: an ordinary browser only ever gets 'local' after explicitly
  // completing OfflineSetup AND on a browser build that can actually back
  // it - isOpfsSqliteSupported() is re-checked on every boot (not cached in
  // the flag itself) so a device that loses OPFS support after enrolling
  // (a rare browser downgrade, or the same profile reopened somewhere that
  // lacks it) falls back to 'api' rather than getting stuck.
  return browserOfflineModeEnabled() && isOpfsSqliteSupported() ? 'local' : 'api';
}

let sourcePromise: Promise<PosDataSource> | null = null;
/**
 * Whose source it is: user and property. A different person signing in, or
 * the same person switching property, gets a fresh source - never the last
 * one's (DPDP Phase 9: a till must not keep showing one user's or one
 * property's data to the next).
 */
let sourceKey: string | null = null;
/**
 * The resolved value of `sourcePromise`, kept in lockstep with it - needed
 * because OfflineSetup.tsx (the offline-PIN enrol/reset flow, now reachable
 * from the kiosk too, not just a browser) has no `await` point of its own
 * to resolve `sourcePromise` from, and must reuse this exact instance's
 * database rather than opening a second one (see LocalPosDataSource.
 * localDatabase's file doc for why a second one would fail outright).
 */
let activeSource: PosDataSource | null = null;
// number, not ReturnType<typeof window.setInterval>: that utility type
// resolves to the LAST overload of an overloaded declaration, and once
// @types/node's ambient globals are anywhere in the compile (pulled in via
// apps/api's devDependency, hoisted to the shared node_modules), that last
// overload is Node's NodeJS.Timeout-returning setInterval - even though the
// actual call below correctly resolves to the browser's number-returning one.
let backgroundSyncIntervalId: number | null = null;
let backgroundSyncOnlineHandler: (() => void) | null = null;
let backgroundSyncOfflineHandler: (() => void) | null = null;

/**
 * Fired on window when a sync pull takes offline sign-in away from the user
 * signed in right now (removed from the property, deactivated, no longer
 * POS staff). lib/auth.tsx signs them out.
 */
export const ACCESS_REVOKED_EVENT = 'kortex:access-revoked';

const keyOf = (session: Session) => `${session.user.id}:${session.property?.id ?? ''}`;

/** The one entry point; memoised per user and property so every page shares one source/database. */
export function initPosDataSource(session: Session): Promise<PosDataSource> {
  const key = keyOf(session);
  if (sourcePromise && sourceKey === key) return sourcePromise;
  // Someone else, or somewhere else: close the old source before opening.
  const closing = sourcePromise ? closePosDataSource() : Promise.resolve();
  sourceKey = key;
  const promise: Promise<PosDataSource> = closing
    .then(() => createSource(session))
    .then((source) => {
      if (sourcePromise === promise) activeSource = source;
      return source;
    })
    .catch((error: unknown) => {
      if (sourcePromise === promise) {
        sourcePromise = null; // allow retry
        sourceKey = null;
      }
      throw error;
    });
  sourcePromise = promise;
  return promise;
}

/**
 * Sign-out, user change and property change: stops the background sync and
 * heartbeat, ends the signed-in user's local session, and closes the local
 * database, so nothing of this session stays open for the next.
 */
export async function closePosDataSource(endSessionFor?: string): Promise<void> {
  const pending = sourcePromise;
  const current = activeSource;
  sourcePromise = null;
  activeSource = null;
  sourceKey = null;
  stopBackgroundWork();
  const source = current ?? (pending ? await pending.catch(() => null) : null);
  // A source still being created when this ran may have started timers.
  stopBackgroundWork();
  if (source instanceof LocalPosDataSource) {
    const db = source.localDatabase;
    if (endSessionFor) {
      await db.driver
        .execute({
          sql: 'UPDATE local_session SET ended_at = ? WHERE user_id = ? AND ended_at IS NULL',
          params: [new Date().toISOString(), endSessionFor],
        })
        .catch(() => undefined);
    }
    await db.close().catch(() => undefined);
  }
}

/**
 * The already-open local database backing the current session, if any -
 * `null` for a plain browser still in 'api' mode, or before boot has
 * resolved. OfflineSetup.tsx uses this to enrol/reset the offline PIN
 * against the live connection instead of opening a competing one.
 */
export function getActiveLocalDatabase(): LocalDatabase | null {
  return activeSource instanceof LocalPosDataSource ? activeSource.localDatabase : null;
}

/**
 * The already-running data source, if boot has resolved - `null` in every
 * other case (before boot resolves, or none has been initialised yet).
 * DevicePairingModal.tsx uses this to send one heartbeat immediately after
 * pairing succeeds (lib/deviceHeartbeat.ts's sendOneHeartbeat()), rather
 * than waiting for the next periodic tick.
 */
export function getActivePosDataSource(): PosDataSource | null {
  return activeSource;
}

/** Test/logout hook. Also stops the background sync timer: without this, a
 *  logout followed by a different session's initPosDataSource() would leave
 *  the PREVIOUS session's interval running forever against a database this
 *  process no longer holds a reference to spin down. */
export function resetPosDataSource(): void {
  sourcePromise = null;
  activeSource = null;
  sourceKey = null;
  stopBackgroundWork();
}

function stopBackgroundWork(): void {
  stopDeviceHeartbeat();
  if (backgroundSyncIntervalId !== null) {
    window.clearInterval(backgroundSyncIntervalId);
    backgroundSyncIntervalId = null;
  }
  if (backgroundSyncOnlineHandler) {
    window.removeEventListener('online', backgroundSyncOnlineHandler);
    backgroundSyncOnlineHandler = null;
  }
  if (backgroundSyncOfflineHandler) {
    window.removeEventListener('offline', backgroundSyncOfflineHandler);
    backgroundSyncOfflineHandler = null;
  }
}

/**
 * Resumes this browser's already-provisioned local database with no
 * session and no network - the one entry point OfflineUnlock.tsx needs,
 * since by definition nobody is signed in yet when it runs. Throws
 * NOT_PROVISIONED if this browser has never completed OfflineSetup; the
 * caller falls back to the ordinary online sign-in form in that case,
 * exactly like a fresh browser always would.
 */
export async function resumeLocalDatabaseForUnlock(): Promise<LocalDatabase> {
  return LocalDatabase.resume(createLocalDriver(), { appVersion: 'web' });
}

async function createSource(session: Session): Promise<PosDataSource> {
  if (posDataMode(session.user.id) === 'api') return new RemoteDataSource();

  let db: LocalDatabase;
  try {
    db = await openOrProvisionLocalDatabase(session);
  } catch (error) {
    // This device's local database belongs to another property and still
    // holds work the server has not got: leave it alone (it syncs when that
    // property is used again) and run this session online.
    if (error instanceof LocalDataError && error.code === 'PROPERTY_MISMATCH') return new RemoteDataSource();
    throw error;
  }
  // Offline-first boot order (prompt SS45): the database is already open and
  // the POS is already usable above this line - the sync engine only starts
  // draining the outbox in the background from here on, never gating boot.
  const syncEngine = new SyncEngine(db, new HttpSyncTransport(), {
    appVersion: 'web',
    onUsersRevoked: (userIds) => {
      if (userIds.includes(session.user.id)) window.dispatchEvent(new CustomEvent(ACCESS_REVOKED_EVENT));
    },
  });
  startBackgroundSync(syncEngine);
  const source = new LocalPosDataSource(db, session.user.id, syncEngine);
  // Phase 7 fleet monitoring (docs/android/PHASE-7-ARCHITECTURE.md) - a
  // no-op call on any device that has not paired yet (lib/deviceHeartbeat.ts).
  // Only ever meaningful on the kiosk: posDataMode() only returns 'local'
  // for a plain browser after explicit Phase 8 offline enrolment, and that
  // is not what Phase 7's device fleet (deviceCode/androidVersion-shaped
  // pairing, "tablet" framing throughout) is about - so this is guarded on
  // isAndroidKiosk() specifically, not just "local mode".
  if (isAndroidKiosk()) startDeviceHeartbeat(source);
  return source;
}

/**
 * Resume this device's local database, provisioning it for the first time
 * if needed. Shared by createSource() (every kiosk boot, and a browser boot
 * once offline mode is already enabled) and OfflineSetup.tsx (a browser's
 * FIRST boot into offline mode, which needs the opened `LocalDatabase`
 * itself - it still has PIN/grant enrolment left to do - rather than a
 * `PosDataSource` wrapping a sync engine).
 */
export async function openOrProvisionLocalDatabase(session: Session): Promise<LocalDatabase> {
  try {
    let db = await LocalDatabase.resume(createLocalDriver(), { appVersion: 'web' });
    if (session.property && db.context.propertyId !== session.property.id) {
      db = await reprovisionForProperty(db, session);
    }
    // Already provisioned: refresh master data in the background when the
    // API is reachable; offline boots proceed on the existing replica.
    void refreshSnapshot(db, session).catch((error) => {
      console.warn('[pos-local] snapshot refresh skipped:', error);
    });
    return db;
  } catch (error) {
    if (!(error instanceof LocalDataError) || error.code !== 'NOT_PROVISIONED') throw error;
    return provisionLocalDatabase(session);
  }
}

/**
 * The local database is bound to one property (its local_device row). When
 * the session is for another, nothing of the old property may be shown: if
 * the server already has everything this device did there, the file is
 * erased and set up for the new property; if not, the old file is left for
 * its own property to sync and this session runs online (PROPERTY_MISMATCH).
 */
async function reprovisionForProperty(db: LocalDatabase, session: Session): Promise<LocalDatabase> {
  const [row] = await db.driver.query({ sql: `SELECT count(*) AS n FROM sync_queue WHERE state <> 'DONE'` });
  const unsynced = Number(row?.n ?? 0);
  await db.close();
  if (unsynced > 0 || !navigator.onLine) {
    throw new LocalDataError(
      'PROPERTY_MISMATCH',
      'This device holds unsent work for another property - it will be used online until that is sent.',
    );
  }
  await wipeLocalDatabaseFile();
  return provisionLocalDatabase(session);
}

/** Deletes every local database file in this origin's OPFS pool. Callers close their source first. */
export async function wipeLocalDatabaseFile(): Promise<void> {
  if (!isOpfsSqliteSupported()) return;
  await new OpfsSqliteDriver(OFFLINE_DB_FILENAME, createOpfsWorker()).wipe();
}

/** Work on this device that the server has not acknowledged yet, by count. */
export async function unsyncedLocalWork(): Promise<number> {
  const db = getActiveLocalDatabase();
  if (!db) return 0;
  const [row] = await db.driver.query({ sql: `SELECT count(*) AS n FROM sync_queue WHERE state <> 'DONE'` });
  return Number(row?.n ?? 0);
}

/**
 * Drives the engine without anything in the POS surface ever waiting on it:
 * an interval for steady draining, plus an immediate attempt on `online` so
 * a till that just got its network back does not sit idle for a full
 * interval with bills still queued.
 */
function startBackgroundSync(engine: SyncEngine): void {
  const POLL_INTERVAL_MS = 20_000;

  engine.setNetworkStatus(navigator.onLine);
  const attempt = () => {
    void engine.syncNow().catch((error) => {
      // syncNow() itself does not throw in normal operation (transport and
      // apply failures are caught internally and turned into status/
      // lastError) - this is a last-resort net so a genuinely unexpected
      // throw never becomes an unhandled rejection in the background timer.
      console.warn('[sync] cycle failed unexpectedly', error);
    });
  };

  backgroundSyncOnlineHandler = () => {
    engine.setNetworkStatus(true);
    attempt();
  };
  backgroundSyncOfflineHandler = () => {
    engine.setNetworkStatus(false);
  };
  window.addEventListener('online', backgroundSyncOnlineHandler);
  window.addEventListener('offline', backgroundSyncOfflineHandler);

  if (navigator.onLine) attempt();
  backgroundSyncIntervalId = window.setInterval(attempt, POLL_INTERVAL_MS);
}

/**
 * Opens a brand-new local database for `session`'s property and applies a
 * first provisioning snapshot from the existing read APIs. Requires a live
 * session AND a reachable server - there is no offline path into this
 * function, only out of it (docs/offline/PHASE-8-ARCHITECTURE.md SS2: a
 * browser enrols in offline mode while online, exactly like a kiosk).
 */
export async function provisionLocalDatabase(session: Session): Promise<LocalDatabase> {
  if (!session.property) {
    throw new Error('Sign in to a property before setting up this device.');
  }
  let profile: PosPropertyProfile;
  try {
    profile = await api.get<PosPropertyProfile>('/api/pos/property');
  } catch (error) {
    console.error('[pos-local] enrolment needs the server once', error);
    throw new Error(
      'This device has not been set up yet. Connect to the internet once so it can download the menu and settings.',
    );
  }

  const context: DeviceContext = {
    deviceId: persistedDeviceId(),
    deviceCode: interimDeviceCode(persistedDeviceId()),
    tenantId: profile.tenantId,
    propertyId: profile.id,
  };
  const db = await LocalDatabase.open(createLocalDriver(), context, {
    appVersion: 'web',
  });
  await applyProvisioningSnapshot(db, await buildSnapshot(profile, session));
  return db;
}

async function refreshSnapshot(db: LocalDatabase, session: Session): Promise<void> {
  const profile = await api.get<PosPropertyProfile>('/api/pos/property');
  await applyProvisioningSnapshot(db, await buildSnapshot(profile, session));
}

interface PosPropertyProfile {
  id: string;
  tenantId: string;
  name: string;
  legalName: string | null;
  address: string | null;
  city: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  gstin: string;
  stateCode: string;
  fssai: string | null;
  currency: string;
  timezone: string;
}

/** Interim pull over the existing read APIs; Phase 4's sync pull replaces this. */
async function buildSnapshot(
  profile: PosPropertyProfile,
  session: Session,
): Promise<ProvisioningSnapshot> {
  const [menu, categories, tables, stays, subscription] = await Promise.all([
    api.get<Array<MenuItemDto & { categoryId: string }>>('/api/menu'),
    api.get<Array<{ id: string; name: string; sortOrder: number }>>('/api/categories'),
    api.get<Array<{ id: string; code: string; seats: number; section: string; isActive?: boolean }>>('/api/tables'),
    api.get<Array<{ id: string; folioNo: string; status: string; roomNumber: string; guestName: string }>>('/api/stays'),
    api.get<{ isEntitled: boolean; planName?: string } | null>('/api/subscription').catch(() => null),
  ]);

  const now = new Date().toISOString();
  return {
    property: {
      id: profile.id,
      name: profile.name,
      legalName: profile.legalName,
      address: profile.address,
      city: profile.city,
      pincode: profile.pincode,
      phone: profile.phone,
      email: profile.email,
      gstin: profile.gstin,
      stateCode: profile.stateCode,
      fssai: profile.fssai,
      currency: profile.currency,
      timezone: profile.timezone,
    },
    users: [
      {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email,
        isActive: true,
        role: session.role ?? 'CASHIER',
      },
    ],
    categories: categories.map((c) => ({ id: c.id, name: c.name, sortOrder: c.sortOrder ?? 0 })),
    menuItems: menu.map((m) => ({
      id: m.id,
      categoryId: m.categoryId,
      sku: m.sku,
      name: m.name,
      pricePaise: toPaise(m.price),
      gstRateBp: Math.round(m.gstRate * 100),
      hsn: m.hsn,
      station: m.station,
      isVegetarian: m.isVegetarian,
      discountPctBp: Math.round((m.discountPct ?? 0) * 100),
      foodCostPctBp: Math.round((m.foodCostPct ?? 0) * 100),
      isActive: m.isActive,
      updatedAt: now,
    })),
    tables: tables.map((t) => ({
      id: t.id,
      code: t.code,
      seats: t.seats,
      section: t.section,
      isActive: t.isActive ?? true,
    })),
    stays: stays.map((s) => ({
      id: s.id,
      folioNo: s.folioNo,
      roomNumber: s.roomNumber,
      guestName: s.guestName,
      status: s.status,
      asOf: now,
    })),
    entitlement: subscription ? { isEntitled: subscription.isEntitled, planName: subscription.planName ?? null } : undefined,
  };
}
